<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useRouter, useRoute, RouterView, RouterLink } from 'vue-router'
import SplashScreenView from '@/views/SplashScreenView.vue'
import { dataManager, type QuestionnaireUserData } from '@/dataManager'
import VueFeather from 'vue-feather'

const router = useRouter()

const route = useRoute()

const dm = dataManager.getInstance()

const backUrl = ref<string|null>('')

const isLoading = ref(true)

const userMenuHidden = ref(true)

const userData = ref<QuestionnaireUserData|null>(calcUserData())

const userName = computed(() => {
  return userData.value?.name == '__yourself__'? 'Tu GoTDAH' : userData.value?.name
})

const ownUsage = computed(() => {
  return userData.value != null && userData.value.age >= 16
})

const userList = ref(calcUserList())

onMounted(() => {
  if (! dm.getCurrentUser() || import.meta.env.VITE_APP_USAGE_ENABLED != 'true') {
    router.push('/login')
  }

  setupUserData()

  // delay so logos are visible
  setTimeout(() => {
    isLoading.value = false
  }, 1000)
})

/** Called from user changed event emitted*/
function setupUserData(): void {
  userData.value = calcUserData()
  userList.value = calcUserList()
}

function calcUserData(): QuestionnaireUserData|null {
  return (dm.getCurrentUser() == null)? null : dm.getCurrentUserData()
}

function calcUserList(): string[] {
  return dm.getUserDatas()
}

function changeUserData(userDataName: string) {
  (document.querySelector('dialog.user-selector') as HTMLDialogElement).close()
  dm.setCurrentUserData(userDataName)
  setupUserData()
  userMenuHidden.value = true
  // TODO: only if current route is not safe, but only home is safe right now
  router.push('/')
}

function showUserSelector() {
  (document.querySelector('dialog.user-selector') as HTMLDialogElement).showModal()
}

async function logout() {
  dm.logout()
  await router.push('/login')
  setupUserData()
}

router.beforeEach((to, from) => {
  if (userData.value == null && to.fullPath != '/login') {
    router.push('/login')
  }
})

router.afterEach((to, from) => {
  userMenuHidden.value = true
})

function backButton(): void {
  if (backUrl.value == 'back') {
    router.back()
  } else if (backUrl.value == null) {
    router.push('/')
  } else {
    router.push(backUrl.value)
  }
}

function reload(): void {
  router.go(0)
}

function isTestingSite(): boolean {
  return import.meta.env.VITE_ENV_VARIABLE != 'production'
}
</script>

<template>
  <header>
    <a v-if="backUrl !== null" @click="backButton" class="back-button"
      ><VueFeather class="back-button-arrow" type="chevron-left"
      /><img alt="GoTDAH logo" class="logo" src="@/assets/logo-gotdah-icon.svg"
    /></a>
    <img v-if="backUrl === null" alt="GoTDAH logo" class="logo"
      src="@/assets/logo-gotdah-icon.svg" />

    <div class="stretcher"></div>

    <div class="title-bar" v-if="userName != null" @click="showUserSelector()"
      :class="{ pushable: dm.getUserDatas().length > 1}">
      <p v-if="router.currentRoute.value.name !== 'login' && !ownUsage" class="super-title">El GoTDAH de</p>
      <p class="title" :class="{ small: userName.length > 10 }"
        >{{ userName }}</p>
    </div>

    <button
      v-if="router.currentRoute.value.name !== 'login'"
      @click="userMenuHidden = !userMenuHidden" class="user-menu">
      <VueFeather type="user"/>
    </button>
  </header>

  <div class="user-menu" :class="{ 'hidden': userMenuHidden }">
    <button @click="showUserSelector()" class="secondary">
      <VueFeather type="users" />
      Cambiar usuario</button>
    <RouterLink to="/configuration" class="secondary">
      <VueFeather type="calendar" />Fechas señaladas</RouterLink>
    <RouterLink to="/about" class="secondary">
      <VueFeather type="globe" />
      Acerca de...</RouterLink>
    <button @click="logout()">
      <VueFeather type="log-out" />Cerrar sesión</button>
    <button
      v-if="isTestingSite()"
      @click="dm.generateRandomResults(); reload();">Generar datos de prueba</button>
    <button
      v-if="isTestingSite()"
      @click="dm.cleanRunsData(); reload()">Borrar datos de cuestionarios</button>
    <button
      v-if="isTestingSite()"
      @click="dm.cleanAllData(); reload()">Borrar todos los datos</button>
  </div>

  <section>
    <RouterView
      @back-button="(url: string|null) => backUrl = url"
      @user-changed="setupUserData"
      v-slot="{ Component }"
      >
      <component :is="Component" :userData="userData" />
    </RouterView>
  </section>

  <SplashScreenView :isLoading="isLoading" />

  <dialog class="user-selector" closedBy="any">
    <h3>Usar GoTDAH para:</h3>
    <button v-for="userDataName in userList"
      @click="changeUserData(userDataName)"
      :class="{ secondary: userData?.name == userDataName }"
      >{{ userDataName == '__yourself__'? 'Ti mismo' : userDataName}}</button>
  </dialog>

</template>

<style scoped>
header {
  height: 50px;
  /* background-color: lightgray;*/
  background-color: #f3f3f3;
  display: flex;
}

header .logo {
  height: 100%;
  padding: 5px;
  padding-left: 20px;
}

section {
  background-color: var(--color-background);
  flex-grow: 1;

  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 5px;

  overflow-x: clip;
  overflow-y: scroll;
}

.back-button {
  transition: 0.15s filter ease-in-out;
  filter: brightness(1);
  cursor: pointer;
}
.back-button:hover {
   filter: brightness(1.2);
}

.back-button .back-button-arrow {
  color: var(--secondary-color);
  height: 100%;
  width: 45px;
}

.back-button img {
  padding-left: 0;
  margin-left: -5px;
}

button.user-menu {
  background-color: var(--secondary-color);
  min-height: auto;
  min-width: auto;
  margin: 3px;
  padding: 0;
  aspect-ratio: 1;
  i {
    padding: 0;
  }
}

.stretcher {
  flex-grow: 1;
}

.title-bar {
  position: absolute;
  left: 90px;
  right: 90px;
}

div.user-menu {
  position: fixed;
  top: 45px;
  right: 0;
  width: 300px;
  max-width: 100%;
  background: white;
  border: 2px solid #b34800;
  border-radius: 8px;
  overflow: hidden;

  transition: 0.2s right ease-in;
  z-index: 1000;

  button {
    border: 2px solid #b34800;
  }
}

div.user-menu.hidden {
  right: -300px;
}

div.user-menu a:not(:first-child), div.user-menu button:not(:first-child) {
  border-top: 2px solid var(--main-color);
}

div.user-menu a, div.user-menu button {
  width: 100%;
  min-height: 50px;

  display: flex;
  align-items: center;
  justify-content: center;

  border: 0;
  margin: 0;
  border-radius: 0;
  box-shadow: 0 0;
  &:not(.secondary):not(:last-child) {
    border-bottom: solid 1px red;
  }

  font-family: var(--title-font);
  text-decoration: none;

  background-color: var(--main-color);
  color: white;

  &:hover {
    filter: brightness(125%);
  }

  &.secondary {
    background-color: white;
    color: var(--main-color);
    &:hover {
      filter: none;
      background-color: #ff680022;
    }
  }

  i {
    height: 25px;
    padding-right: 5px;
  }
}

.title-bar {
  text-align: center;
  .super-title {
    font-size: 10pt;
    margin-top: 0;
  }
  .title {
    font-family: var(--title-font);
    margin-top: 0;
    font-size: 14pt;
    &:first-child {
      margin-top: 10px;
    }
    &.small {
      font-size: 10pt;
    }
  }
}

.user-selector button {
  width: 100%;
}

.pushable {
  cursor: pointer;
}

</style>
